import { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyPair } from '@ownerone/lib-crypto/dist/crypto/types';

import { useNotices } from '@/features/notices/useNotices';
import { EncryptionType } from '@/shared/lib/secure-json/core/crypto-core/types';
import { SecureJsonBase } from '@/shared/lib/secure-json/core/secure-json-base';
import { CollectionsSjMap, useSJDatabase } from '@/shared/lib/sj-orm';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { collectionsSjMapToList } from '@/shared/lib/sj-orm/helpers/collections';
import { appInitialFetchCollections } from '@/shared/lib/sj-orm/helpers/lists';
import {
  skipLocalMigrations,
  UpdateCollectionsUpdatedCollections,
  useMigrator,
} from '@/shared/lib/sj-orm/migrator';
import { useCollectionsStatusStore } from '@/shared/lib/sj-orm/stores/collectionsStatus.store';
import { useSecureJsonCollectionsStore } from '@/shared/lib/sj-orm/stores/secureJsonCollections.store';
import { Synchronizer } from '@/shared/lib/sj-orm/synchronizer/core';
import { useLoader } from '@/shared/lib/sj-orm/synchronizer/hooks/useLoader';
import { validateCollections } from '@/shared/lib/sj-orm/validator';
import { log } from '@/shared/utils/log';

import { useUpdateCollectionsByUser } from './useUpdateCollectionsByUser';
import { useUpdateMigratedCollections } from './useUpdateMigratedCollections';

type FetchResult = {
  error?: boolean;
  collections?: string[];
};

/**
 * TODO remove reactive. Load collections / migrate collections / validate collections
 */
export const useCollectionsInit = () => {
  const { loader } = useLoader();

  const notices = useNotices();
  const colStatusStore = useCollectionsStatusStore();
  const sjcStore = useSecureJsonCollectionsStore();
  const sjdb = useSJDatabase();
  const { updateCollections } = useUpdateCollectionsByUser();
  const migrator = useMigrator(updateCollections);
  const { updateMigratedCollections } = useUpdateMigratedCollections();
  // TODO rework, update items with api
  const [validated, setValidated] = useState<UpdateCollectionsUpdatedCollections | null>(null);

  const initialFetchCollections = useCallback(
    async (keyPair: KeyPair, encryptionType: EncryptionType): Promise<FetchResult> => {
      if (!encryptionType) {
        log.error('[useCollectionsInit] empty encryptionType');
        return { error: true };
      }

      if (!keyPair) {
        log.error('[useCollectionsInit] empty keyPair');
        return { error: true };
      }
      log.trace('[useCollectionsInit] initializing');
      // TODO WA-1486 set loading status
      colStatusStore.setStatus('initializing');

      const errorCollections: string[] = [];
      const initialCollections: CollectionsSjMap = new Map();

      /* Fetch collections */
      const onCollectionLoaded = (name: CollectionName, collection: SecureJsonBase) => {
        initialCollections.set(name, collection);
        sjcStore.setCollection(name, collection);
      };

      const synchronizer = new Synchronizer(encryptionType, keyPair, loader, onCollectionLoaded);
      log.trace('[useCollectionsInit] pull collections');
      await Promise.allSettled(
        appInitialFetchCollections.map((iName) => {
          return synchronizer.pull(iName).catch((error) => {
            log.error('[useCollectionsInit] pull collection error', { iName, error });
            errorCollections.push(iName);
          });
        }),
      );
      if (errorCollections.length) {
        log.error('[useCollectionsInit] pull collections failed', { errorCollections });
        return { error: true, collections: errorCollections };
      }

      log.trace('[useCollectionsInit] migrations');
      /* Migrate collections */
      if (!skipLocalMigrations() && migrator.shouldMigrate()) {
        const resMigration = await migrator.migrateCollections({
          initialCollections,
        });

        if (resMigration.error) {
          notices.error(`[useCollectionsInit] ${resMigration.error}`, { delay: 10 });
          colStatusStore.setIsMigrationError(true);
          return { error: true };
        }

        if (!resMigration.error && resMigration.data) {
          const resCollections = await updateMigratedCollections({
            schemaVersion: resMigration.data.schemaVersion,
            encryptionType,
            keyPair,
            migratedData: resMigration.data.data,
            initialCollections,
          });

          resCollections.forEach(({ name, collection }) => {
            sjcStore.setCollection(name, collection);
          });
        }

        log.trace('[useCollectionsInit] Migrate collections done');
      } else {
        log.trace('[useCollectionsInit] Migrate collections skip');
      }

      /* Validate collections */
      log.trace('[useCollectionsInit] validations');
      // TODO check reactive
      const cols = collectionsSjMapToList(useSecureJsonCollectionsStore.getState().collections);
      const brokenCollections = validateCollections(cols);

      if (brokenCollections.length > 0) {
        const res = await updateCollections({ collections: brokenCollections });
        if (res.collections.size) {
          setValidated(res.collections);
        }
        log.trace('[useCollectionsInit] Validate collections done');
      } else {
        log.trace('[useCollectionsInit] Validate collections valid');
      }

      log.trace('[useCollectionsInit] inited');
      colStatusStore.setStatus('inited');

      return {};
    },
    [loader, migrator],
  );

  useEffect(() => {
    if (!validated) {
      return;
    }

    validated.forEach((items, name) => {
      items.forEach((data) => {
        // @ts-ignore
        sjdb.collections[name]?.update({
          ...data,
          // country: 1,
          // purchaseValue: {...data.purchaseValue, value: '123' }
        });
      });
    });

    setValidated(null);
  }, [validated]);

  return useMemo(
    () => ({
      initialFetchCollections,
    }),
    [initialFetchCollections],
  );
};
